/**
 * „Kto ma którą" — the grid, the card beside it, and the line underneath.
 *
 * ── The box is the same height in all five states ────────────────────────────────────────────
 * `pending`, `error`, `noClients`, `noSurveys` and `rows` all stand inside a card whose scrollport
 * carries `--h-report-matrix-min`, so the note under it never moves and the page never reflows when
 * the read lands. The skeleton is a head band and eight rows at the real heights, which is what
 * makes it FIT the reserve instead of shrinking the card when the answer arrives.
 *
 * ── The write's outcome is announced, because the screen shows it quietly ────────────────────
 * A tick appearing in one cell of a hundred-and-twenty is invisible to a reader and easy to miss
 * with the eye. The live region says who now has what; the failure says so in the note row, where
 * the trainer is already looking for the „Zapisano" line the canon draws there.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '~/shared/ui/EmptyState';
import { InboxMarkIcon, NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import ghost from '~/shared/ui/ghostButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';

import { columnTally } from '../model/matrix';
import { useMatrixTab } from '../lib/useMatrixTab';
import { MatrixCard } from './MatrixCard';
import { MatrixTable } from './MatrixTable';
import styles from './Matrix.module.css';

/** Eight rows and five ticks — the canon's own case, and what the reserve was derived from. */
const SKELETON_ROWS = 8;
const SKELETON_TICKS = 5;

function MatrixSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();

  return (
    <div className={styles.skelWait} aria-busy="true">
      <p className={hidden.srOnly}>{t('web:reports.matrix.states.loading')}</p>
      <div className={styles.skelHead} />
      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
        <div key={row} className={styles.skelRow}>
          {skeleton ? (
            <>
              <i className={`${String(skel.bar)} ${String(styles.skelName)}`} />
              {Array.from({ length: SKELETON_TICKS }, (_, tick) => (
                <i key={tick} className={`${String(skel.bar)} ${String(styles.skelTick)}`} />
              ))}
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function MatrixTab() {
  const { t } = useTranslation();
  const announcer = useAnnouncer();
  const matrix = useMatrixTab(announcer.announce);

  const selectedColumn =
    matrix.selected === null
      ? null
      : (matrix.view.columns.find((column) => column.id === matrix.selected?.id) ?? null);

  return (
    <div className={styles.tab}>
      <div className={styles.left}>
        <div className={styles.card}>
          {matrix.state === 'pending' ? (
            <MatrixSkeleton skeleton={matrix.skeleton} />
          ) : matrix.state === 'rows' ? (
            // `tabIndex` so a scrollport with no focusable ancestor of its own can be scrolled from
            // the keyboard — the archive's rule, and the checkboxes inside are focusable anyway.
            <div className={styles.scroll} tabIndex={0}>
              <MatrixTable
                view={matrix.view}
                selectedId={matrix.selected?.id ?? null}
                onSelectColumn={matrix.onSelectColumn}
                isBusy={matrix.isBusy}
                onToggle={matrix.onToggle}
                reveal={matrix.reveal}
              />
            </div>
          ) : (
            <div className={styles.state}>
              {matrix.state === 'error' ? (
                <>
                  <EmptyState
                    mark={<NoteMarkIcon />}
                    tone="neutral"
                    title={t('web:reports.matrix.states.errorTitle')}
                    hint={t('web:reports.matrix.states.errorMessage')}
                  />
                  <button
                    type="button"
                    className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                    onClick={matrix.onRetry}
                  >
                    {t('web:reports.matrix.states.retry')}
                  </button>
                </>
              ) : (
                <>
                  <EmptyState
                    mark={<InboxMarkIcon />}
                    tone="action"
                    title={t(
                      matrix.state === 'noClients'
                        ? 'web:reports.matrix.emptyClients.title'
                        : 'web:reports.matrix.emptySurveys.title',
                    )}
                    hint={t(
                      matrix.state === 'noClients'
                        ? 'web:reports.matrix.emptyClients.message'
                        : 'web:reports.matrix.emptySurveys.message',
                    )}
                  />
                  {/* Only the survey-shaped emptiness has an act behind it. „Nie masz jeszcze
                    * klientów" is answered by an invitation this screen does not own. */}
                  {matrix.state === 'noSurveys' ? (
                    <Link
                      to="/raporty/nowa"
                      className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                    >
                      {t('web:reports.surveys.empty.action')}
                    </Link>
                  ) : null}
                </>
              )}
            </div>
          )}
        </div>

        {/* Only what the trainer has to know NOW: a paused column refusing new ticks, and a write
          * that did not land. The standing footnote on how the grid scrolls went on 4 November 2026
          * (owner) — the grid shows it. */}
        {(selectedColumn !== null && !selectedColumn.active) || matrix.writeFailed ? (
          <p className={styles.note}>
            <span className={styles.noteText}>
              {selectedColumn !== null && !selectedColumn.active
                ? t('web:reports.matrix.pausedNote')
                : null}
            </span>
            {matrix.writeFailed ? (
              <span className={styles.noteFailed}>{t('web:reports.matrix.writeFailed')}</span>
            ) : null}
          </p>
        ) : null}
      </div>

      <MatrixCard
        state={matrix.cardState}
        skeleton={matrix.cardSkeleton}
        reveal={matrix.cardReveal}
        row={matrix.selected}
        sections={matrix.sections}
        tally={selectedColumn === null ? null : columnTally(selectedColumn, matrix.view)}
      />

      <LiveRegionAnnouncer message={announcer.message} />
    </div>
  );
}
